import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, ValidatorFn } from '@angular/forms';
import { MatChipGrid } from '@angular/material/chips';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { simplifyStringForSearch } from '@app/shared/extra/utils';
import { Observable } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'autocomplete-field-builder',
  templateUrl: './autocomplete-field-builder.component.html',
  styleUrls: ['./autocomplete-field-builder.component.scss'],
  standalone: false
})
export class AutocompleteFieldBuilderComponent extends AbstractFieldBuilder<FormControl<string>> implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;
  public filteredOptions: Observable<string[]>;
  @ViewChild('chipList') public chipList: MatChipGrid;
  @ViewChild('inputField') public inputField: ElementRef;

  public ngOnInit(): void {
    this.form = new FormGroup({
      field: new FormControl(
        this.fieldInitValue,
        this.computeValidators()
      )
    });
    // Propagate invalid status to the mat-chip-list
    this.form.controls.field.statusChanges.subscribe(
      (status: FormControlStatus) => {
        if (this.chipList !== void 0) {
          this.chipList.errorState = status === 'INVALID';
        }
      });

    // Initialise the field in the registry
    this.setFieldValue(this.fieldInitValue);
    this.setFieldInitialValue(this.fieldInitValue);
    this.getNextState();

    // Listen for value changes to update filtered values
    this.filteredOptions = this.form.controls.field.valueChanges
      .pipe(
        startWith(''),
        filter(value => value !== void 0),
        map(value => {
          return this.fieldConfig.field.fieldValues?.filter(option =>
            simplifyStringForSearch(option).includes(simplifyStringForSearch(value))
          );
        })
      );

    // Refresh values after save
    this.formStateService.saved$.pipe(
      filter(({code}) => {
        const fieldValue = this.form.controls.field.value;
        return !!fieldValue
          && code === this.fieldConfig.fieldCode
          && !this.fieldConfig.field.fieldValues.includes(fieldValue);
      }),
      tap(() => this.fieldConfig.field.fieldValues.push(this.form.controls.field.value)),
      // Resubscribe to the saved observable if an error occurred.
      catchError(() => this.formStateService.saved$),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    this.form.controls.field.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      this.setFieldValue(value);
    });
  }

  public cancel(): void {
    this.form.controls.field.setValue(this.getFieldInitialValue());
    super.cancel();
  }

  protected computeValidators(): ValidatorFn {
    if (this.fieldConfig.field.fieldType === FieldTypeEnum.TEXT_AUTOCOMPLETE) {
      this.fieldConfig.field?.validators.push(new FieldValidator(
        ValidatorType.NOT_IN_ARRAY,
        this.fieldConfig.field.fieldValues
      ));
    }
    return super.computeValidators();
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => this.inputField.nativeElement.focus());
    }
  }
}
